import { useMemo } from 'react';
import { calculateGuideBounds } from '../utils/faceGuidanceLogic';
import { HeadTurnArrowCue } from './HeadTurnArrowCue';
import './FaceGuideOverlay.css';

/**
 * Get device type based on screen width
 * @returns {string} Device type: 'mobile', 'tablet', 'small-laptop', or 'desktop'
 */
const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  if (width < 1440) return 'small-laptop';
  return 'desktop';
};

/**
 * Calculate corner indicator length based on device type
 * @returns {number} Corner indicator length in pixels
 */
const getCornerIndicatorLength = () => {
  const deviceType = getDeviceType();
  switch (deviceType) {
    case 'mobile':
      return 15; // Smaller for mobile
    case 'tablet':
      return 18; // Medium for tablet
    case 'small-laptop':
      return 20; // Standard for small laptop
    case 'desktop':
    default:
      return 20; // Standard for desktop
  }
};

/**
 * Calculate inner glow offset based on device type
 * @returns {number} Inner glow offset in pixels
 */
const getInnerGlowOffset = () => {
  const deviceType = getDeviceType();
  switch (deviceType) {
    case 'mobile':
      return 4; // Smaller offset for mobile
    case 'tablet':
      return 5; // Medium offset for tablet
    case 'small-laptop':
      return 6; // Standard offset for small laptop
    case 'desktop':
    default:
      return 6; // Standard offset for desktop
  }
};

/**
 * FaceGuideOverlay Component
 * Displays a semi-transparent square overlay with beveled corners
 * and color-coded feedback with real-time alignment instructions
 */
const FaceGuideOverlay = ({ 
  statusColor = 'red', 
  message = 'Position your face',
  videoWidth = 640,
  videoHeight = 480,
  showOverlay = true,
  countdown = null,
  lightingOk = false,
  faceDetected = false,
  faceStraight = false,
  voiceDisabled = false,
  /** 'left' | 'right' = which way user should turn their head; omit during front capture */
  headTurnDirection = null
}) => {
  // Calculate guide dimensions using centralized function to avoid duplication
  const guideDimensions = useMemo(() => {
    const bounds = calculateGuideBounds(videoWidth, videoHeight);
    const topPercent = (bounds.centerY - bounds.height / 2) / videoHeight;
    const leftPercent = (bounds.centerX - bounds.width / 2) / videoWidth;
    
    return {
      centerX: bounds.centerX,
      centerY: bounds.centerY,
      width: bounds.width,
      height: bounds.height,
      topPercent,
      leftPercent,
    };
  }, [videoWidth, videoHeight]);

  // Calculate responsive corner indicator length and inner glow offset
  const cornerLength = useMemo(() => getCornerIndicatorLength(), []);
  const innerGlowOffset = useMemo(() => getInnerGlowOffset(), []);

  if (!showOverlay) {
    return null;
  }

  // Color mapping (orange = bad lighting)
  const colorMap = {
    green: {
      stroke: '#4ade80',
      fill: 'rgba(74, 222, 128, 0.1)',
      text: '#22c55e',
    },
    yellow: {
      stroke: '#f59e0b',
      fill: 'rgba(245, 158, 11, 0.18)',
      text: '#92400e',
    },
    red: {
      stroke: '#ef4444',
      fill: 'rgba(239, 68, 68, 0.1)',
      text: '#dc2626',
    },
    orange: {
      stroke: '#f97316',
      fill: 'rgba(249, 115, 22, 0.1)',
      text: '#ea580c',
    },
  };

  const colors = colorMap[statusColor] || colorMap.red;

  return (
    <div className="ny-skin-fg">
      <svg
        className="ny-skin-fg__svg"
        width="100%"
        height="100%"
        viewBox={`0 0 ${videoWidth} ${videoHeight}`}
        preserveAspectRatio="none"
      >
        <rect
          className="ny-skin-fg__rect"
          x={guideDimensions.centerX - guideDimensions.width / 2}
          y={guideDimensions.centerY - guideDimensions.height / 2}
          width={guideDimensions.width}
          height={guideDimensions.height}
          rx={guideDimensions.width * 0.12}
          ry={guideDimensions.width * 0.12}
          fill="none"
          stroke={colors.stroke}
          strokeWidth="4"
          strokeDasharray={statusColor === 'green' ? '0' : '12,6'}
          opacity={0.9}
        />

        {statusColor === 'green' && (
          <rect
            className="ny-skin-fg__inner-rect"
            x={guideDimensions.centerX - guideDimensions.width / 2 + innerGlowOffset}
            y={guideDimensions.centerY - guideDimensions.height / 2 + innerGlowOffset}
            width={guideDimensions.width - (innerGlowOffset * 2)}
            height={guideDimensions.height - (innerGlowOffset * 2)}
            rx={guideDimensions.width * 0.12}
            ry={guideDimensions.width * 0.12}
            fill="none"
            stroke={colors.stroke}
            strokeWidth="2"
            opacity={0.5}
          />
        )}

        {statusColor !== 'green' && (
          <>
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX - guideDimensions.width / 2}
              y1={guideDimensions.centerY - guideDimensions.height / 2 + cornerLength}
              x2={guideDimensions.centerX - guideDimensions.width / 2}
              y2={guideDimensions.centerY - guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX - guideDimensions.width / 2 + cornerLength}
              y1={guideDimensions.centerY - guideDimensions.height / 2}
              x2={guideDimensions.centerX - guideDimensions.width / 2}
              y2={guideDimensions.centerY - guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX + guideDimensions.width / 2}
              y1={guideDimensions.centerY - guideDimensions.height / 2 + cornerLength}
              x2={guideDimensions.centerX + guideDimensions.width / 2}
              y2={guideDimensions.centerY - guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX + guideDimensions.width / 2 - cornerLength}
              y1={guideDimensions.centerY - guideDimensions.height / 2}
              x2={guideDimensions.centerX + guideDimensions.width / 2}
              y2={guideDimensions.centerY - guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX - guideDimensions.width / 2}
              y1={guideDimensions.centerY + guideDimensions.height / 2 - cornerLength}
              x2={guideDimensions.centerX - guideDimensions.width / 2}
              y2={guideDimensions.centerY + guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX - guideDimensions.width / 2 + cornerLength}
              y1={guideDimensions.centerY + guideDimensions.height / 2}
              x2={guideDimensions.centerX - guideDimensions.width / 2}
              y2={guideDimensions.centerY + guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX + guideDimensions.width / 2}
              y1={guideDimensions.centerY + guideDimensions.height / 2 - cornerLength}
              x2={guideDimensions.centerX + guideDimensions.width / 2}
              y2={guideDimensions.centerY + guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
            <line
              className="ny-skin-fg__corner"
              x1={guideDimensions.centerX + guideDimensions.width / 2 - cornerLength}
              y1={guideDimensions.centerY + guideDimensions.height / 2}
              x2={guideDimensions.centerX + guideDimensions.width / 2}
              y2={guideDimensions.centerY + guideDimensions.height / 2}
              stroke={colors.stroke}
              strokeWidth="3"
              opacity={0.6}
            />
          </>
        )}

        {(headTurnDirection === 'left' || headTurnDirection === 'right') && (
          <g
            className={`ny-skin-fg__head-turn ny-skin-fg__head-turn--${headTurnDirection}`}
            stroke={colors.stroke}
            fill="none"
            strokeWidth={Math.max(3, videoWidth * 0.004)}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.88}
          >
            {headTurnDirection === 'right' ? (
              <g transform={`translate(${guideDimensions.centerX + guideDimensions.width / 2 + videoWidth * 0.02}, ${guideDimensions.centerY})`}>
                <g className="ny-skin-fg__head-turn-shift">
                  <polyline points="-6,-22 10,0 -6,22" />
                  <polyline points="10,-22 26,0 10,22" />
                  <polyline points="26,-22 42,0 26,22" />
                </g>
              </g>
            ) : (
              <g transform={`translate(${guideDimensions.centerX - guideDimensions.width / 2 - videoWidth * 0.02}, ${guideDimensions.centerY})`}>
                <g className="ny-skin-fg__head-turn-shift">
                  <polyline points="6,-22 -10,0 6,22" />
                  <polyline points="-10,-22 -26,0 -10,22" />
                  <polyline points="-26,-22 -42,0 -26,22" />
                </g>
              </g>
            )}
          </g>
        )}
      </svg>

      <div className="ny-skin-fg__status">
        <div className="ny-skin-fg__status-item">
          <span className={`ny-skin-fg__status-icon ${lightingOk ? 'ny-skin-fg__status-icon--ok' : 'ny-skin-fg__status-icon--bad'}`}>
            {lightingOk ? '✓' : '✗'}
          </span>
          <span className="ny-skin-fg__status-label">Good Lighting</span>
        </div>
        <div className="ny-skin-fg__status-item">
          <span className={`ny-skin-fg__status-icon ${faceDetected ? 'ny-skin-fg__status-icon--ok' : 'ny-skin-fg__status-icon--bad'}`}>
            {faceDetected ? '✓' : '✗'}
          </span>
          <span className="ny-skin-fg__status-label">Face Detected</span>
        </div>
        <div className="ny-skin-fg__status-item">
          <span className={`ny-skin-fg__status-icon ${faceStraight ? 'ny-skin-fg__status-icon--ok' : 'ny-skin-fg__status-icon--bad'}`}>
            {faceStraight ? '✓' : '✗'}
          </span>
          <span className="ny-skin-fg__status-label">Straight Face</span>
        </div>
      </div>

      {countdown !== null && countdown > 0 ? (
        <div
          className="ny-skin-fg__countdown"
          style={{
            top: `${(guideDimensions.centerY / videoHeight) * 100}%`,
            left: `${(guideDimensions.centerX / videoWidth) * 100}%`,
          }}
        >
          <span className="ny-skin-fg__countdown-num">{countdown}</span>
        </div>
      ) : (
        <div
          className="ny-skin-fg__message"
          style={{
            top: `${(guideDimensions.centerY / videoHeight) * 100}%`,
            left: `${(guideDimensions.centerX / videoWidth) * 100}%`,
            color: colors.stroke,
          }}
        >
          {message}
          {(headTurnDirection === 'left' || headTurnDirection === 'right') && (
            <HeadTurnArrowCue direction={headTurnDirection} accentColor={colors.stroke} />
          )}
          {voiceDisabled && (
            <span className="ny-skin-fg__message-hint">Follow the display text</span>
          )}
        </div>
      )}
    </div>
  );
};

export default FaceGuideOverlay;

